import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, BookOpen } from 'lucide-react';
import { getApiUrl, resolveImageUrl } from '../utils/api';
import '../styles/book-flip.css';

const Portfolio = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipping, setIsFlipping] = useState(false);
  const [flipDirection, setFlipDirection] = useState('next');
  const [nextIndex, setNextIndex] = useState(0);
  
  // State for data from backend
  const [services, setServices] = useState([]);
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch data from backend on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const apiUrl = getApiUrl();
        const [servicesRes, portfolioRes] = await Promise.all([
          fetch(`${apiUrl}/api/services`),
          fetch(`${apiUrl}/api/portfolio`)
        ]);
        
        const [servicesData, portfolioData] = await Promise.all([
          servicesRes.json(),
          portfolioRes.json()
        ]);
        
        setServices(servicesData);
        setPortfolioItems(portfolioData);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  const currentItem = portfolioItems[currentIndex];
  const nextItem = portfolioItems[nextIndex];

  const handleNext = () => {
    if (!isFlipping && currentIndex < portfolioItems.length - 1) {
      const newIndex = currentIndex + 1;
      setNextIndex(newIndex);
      setFlipDirection('next');
      setIsFlipping(true);
      
      setTimeout(() => {
        setCurrentIndex(newIndex);
        setIsFlipping(false);
      }, 800);
    }
  };

  const handlePrevious = () => {
    if (!isFlipping && currentIndex > 0) {
      const newIndex = currentIndex - 1;
      setNextIndex(newIndex);
      setFlipDirection('prev');
      setIsFlipping(true);
      
      setTimeout(() => {
        setCurrentIndex(newIndex);
        setIsFlipping(false);
      }, 800);
    }
  };

  // Get chapter info for current item
  const getChapterInfo = (item) => {
    const service = services.find(s => s.id === item?.category);
    return service?.name || item?.category || 'Gallery';
  };

  if (loading) {
    return (
      <div className="portfolio-page" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: '#b8860b', fontStyle: 'italic', fontSize: '1.2rem' }}>Loading portfolio...</p>
      </div>
    );
  }

  if (portfolioItems.length === 0) {
    return (
      <div className="portfolio-page">
        <section className="portfolio-hero">
          <div className="marble-overlay"></div>
          <div className="container">
            <div className="portfolio-hero-content">
              <BookOpen size={48} className="book-icon" />
              <h1 className="hero-medium">Our Transformation Portfolio</h1>
              <p className="body-large">Portfolio is being prepared. Check back soon!</p>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="portfolio-page">
      {/* Hero Section */}
      <section className="portfolio-hero">
        <div className="marble-overlay"></div>
        <div className="container">
          <div className="portfolio-hero-content">
            <BookOpen size={48} className="book-icon" />
            <h1 className="hero-medium">Our Transformation Portfolio</h1>
            <p className="body-large">Flowing through all our beautiful work - Click arrows to turn pages</p>
          </div>
        </div>
      </section>

      {/* Book Section */}
      <section className="portfolio-content section-padding">
        <div className="container">
          {currentItem && (
            <div className="flip-book-container">
              {/* The Open Book */}
              <div className="flip-book">
                {/* Left Page (static) */}
                <div className="book-page book-page-left">
                  <div 
                    className="page-image-half page-image-left"
                    style={{ 
                      backgroundImage: `url(${resolveImageUrl(currentItem.beforeImage)})`,
                      backgroundPosition: 'left center'
                    }}
                  />
                </div>

                {/* Right Page (static - shows under flipping page) */}
                <div className="book-page book-page-right">
                  <div 
                    className="page-image-half page-image-right"
                    style={{ 
                      backgroundImage: `url(${resolveImageUrl(isFlipping ? nextItem?.beforeImage : currentItem.beforeImage)})`,
                      backgroundPosition: 'right center'
                    }}
                  />
                </div>

                {/* Flipping Page Overlay */}
                {isFlipping && (
                  <div className={`flipping-page ${flipDirection === 'next' ? 'flip-to-left' : 'flip-to-right'}`}>
                    {/* Front of flipping page (current image right half) */}
                    <div className="flip-page-front">
                      <div 
                        className="page-image-half"
                        style={{ 
                          backgroundImage: `url(${resolveImageUrl(flipDirection === 'next' ? currentItem.beforeImage : nextItem?.beforeImage)})`,
                          backgroundPosition: 'right center'
                        }}
                      />
                    </div>
                    {/* Back of flipping page (next image left half) */}
                    <div className="flip-page-back">
                      <div 
                        className="page-image-half"
                        style={{ 
                          backgroundImage: `url(${resolveImageUrl(flipDirection === 'next' ? nextItem?.beforeImage : currentItem.beforeImage)})`,
                          backgroundPosition: 'left center'
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Book Spine (center) */}
                <div className={`book-spine ${isFlipping ? 'spine-visible' : ''}`}></div>

                {/* Gold Frame Border */}
                <div className="book-gold-frame"></div>
              </div>

              {/* Navigation Arrows */}
              <button 
                className="flip-nav flip-nav-left"
                onClick={handlePrevious}
                disabled={isFlipping || currentIndex === 0}
                aria-label="Previous page"
              >
                <ChevronLeft size={28} />
              </button>
              <button 
                className="flip-nav flip-nav-right"
                onClick={handleNext}
                disabled={isFlipping || currentIndex === portfolioItems.length - 1}
                aria-label="Next page"
              >
                <ChevronRight size={28} />
              </button>

              {/* Treatment Info Below Book */}
              <div className="flip-book-info">
                <div className="chapter-badge-gold">
                  {getChapterInfo(currentItem)}
                </div>
                <h3 className="treatment-title-gold">{currentItem.treatmentName}</h3>
                <p className="treatment-desc">{currentItem.description}</p>
                <div className="page-indicator">
                  <span>Page {currentIndex + 1} of {portfolioItems.length}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Portfolio;
